import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["availableOperation", "disabledReason", "secondaryButtonUse", "primaryButtonUse"];
import { useMemo } from 'react';
import { Button } from 'foundations-components/transitional/components/Button';
import { ApprovalOperationI18n } from '../i18n/ApprovalOperationI18n';
import { withErrorBoundary } from '../../hoc/withErrorBoundary';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { useApprovalContext } from '../../hooks/useApprovalContext';
import { ApprovalOperation } from 'ui-product-approvals-common';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useApprovalConditions } from '../../hooks/useApprovalConditions';
import { ApprovalDataLoadingSpinner } from '../common/ApprovalDataLoadingSpinner';
import { ApprovalNotReadyI18nBody, ApprovalNotReadyI18nFetching } from '../i18n/ApprovalNotReadyI18n';
import { jsx as _jsx } from "react/jsx-runtime";
// NOTE: This needs to be carefully migrated due to downstream usage. Will have
// to be updated ahead of Team actor rollout. It still backs the ungated sidebar
// footer, the redesign footer's REQUEST/CANCEL buttons, and external consumers
// (e.g. ContentEditorUI); the redesign's per-step Approve/Reject/Request-changes
// actions go through ApprovalStageActionsRedesign + the shared modal, not
// this component.
const ApprovalButton = _ref => {
  let {
      availableOperation,
      disabledReason,
      secondaryButtonUse,
      primaryButtonUse
    } = _ref,
    buttonProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const approval = useApprovalContext();
  const {
    isAwaitingApprovableObject,
    isFetchComplete,
    isLoading
  } = useApprovalConditions();

  // The meta.approvalOperation is used to support the ContenttEditorUI - they use their own
  // logic instead of the ApprovalSidebarButtons, and they do not currently use 'REJECT'.
  // (ContentEditorUI/static/js/components/approvals/EditorApprovalButtons.tsx)
  const approvalOperation = availableOperation || approval.meta.approvalOperation;
  const use = useMemo(() => {
    if (approvalOperation === ApprovalOperation.CANCEL) {
      return secondaryButtonUse || 'secondary';
    }
    if (approvalOperation === ApprovalOperation.REJECT) {
      return secondaryButtonUse || 'secondary';
    }
    if (approvalOperation === ApprovalOperation.REQUEST_CHANGES) {
      return secondaryButtonUse || 'secondary';
    }
    return primaryButtonUse || 'primary';
  }, [approvalOperation, primaryButtonUse, secondaryButtonUse]);
  const onClickButton = () => {
    approval.meta.setCurrentOperation(approvalOperation);
    approval.meta.setShowModal(true);
  };

  // Added loading check here as some apps are using ApprovalButton directly
  if (!isFetchComplete && (!isAwaitingApprovableObject || isLoading)) {
    return /*#__PURE__*/_jsx(ApprovalDataLoadingSpinner, {});
  }
  if (!approvalOperation) {
    return null;
  }
  const isCancelOperation = approvalOperation === ApprovalOperation.CANCEL;
  const isCallerDisabled = Boolean(buttonProps.disabled);
  const hasErrorFetchingApprovableObjectId = Boolean(approval.meta.hasErrorFetchingApprovableObjectId);
  if (!isCallerDisabled && !hasErrorFetchingApprovableObjectId && isAwaitingApprovableObject && approval.meta.isFetchingApprovableObjectId) {
    return /*#__PURE__*/_jsx(ApprovalDataLoadingSpinner, {
      tooltipTitle: /*#__PURE__*/_jsx(ApprovalNotReadyI18nFetching, {})
    });
  }
  const isUnavailableApprovableObject = hasErrorFetchingApprovableObjectId || isAwaitingApprovableObject;
  const isButtonDisabled = isCallerDisabled || isUnavailableApprovableObject;
  const disabledTooltipTitle = isCallerDisabled ? disabledReason !== null && disabledReason !== void 0 ? disabledReason : /*#__PURE__*/_jsx(FormattedMessage, {
    message: "productApprovals.noAccessTooltip"
  }) : hasErrorFetchingApprovableObjectId ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "productApprovals.ApprovalDataError.content"
  }) : isAwaitingApprovableObject ? /*#__PURE__*/_jsx(ApprovalNotReadyI18nBody, {}) : null;
  const tooltipTitle = isButtonDisabled ? disabledTooltipTitle : isCancelOperation ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "productApprovals.cancelOperationTooltip"
  }) : null;
  const isTooltipDisabled = !isButtonDisabled && !isCancelOperation;
  return /*#__PURE__*/_jsx(Tooltip, {
    title: tooltipTitle,
    disabled: isTooltipDisabled,
    children: /*#__PURE__*/_jsx(Button, Object.assign({
      role: "button"
    }, buttonProps, {
      disabled: isButtonDisabled,
      use: use,
      "data-test-id": `product-approvals-approval-button-${approvalOperation}`,
      onClick: onClickButton,
      children: /*#__PURE__*/_jsx(ApprovalOperationI18n, {
        approvalOperation: approvalOperation,
        approvalType: approval.meta.approvalType
      })
    }))
  });
};

/**
 * @remarks
 * Consumer styling requirement: add the import below to the consuming app's
 * `project.sass`. Requesting an approval opens the approval modal's
 * hubspot-prosemirror note editor, which renders unstyled without it.
 *
 * @example
 * ```sass
 * @import hubspot-prosemirror/sass/project
 * ```
 */
const ApprovalButtonWithErrorBoundary = withErrorBoundary(ApprovalButton, 'ApprovalButton');
export { ApprovalButtonWithErrorBoundary as ApprovalButton };